import { useState } from "react";
import { Box, Button, Typography, TextField, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslate } from "../../components/Translate/TranslateContext";

const DATE_FIELDS = ["first_sprout", "first_cutting", "first_flower", "harvest_date"];

export default function GardenHarvest({ garden, onNext }) {
    const { translate } = useTranslate();

    const metricFields = garden?.crop_metrics || [];

    const GRID_COLUMNS = `110px ${metricFields.map(() => "140px").join(" ")} 180px 180px 180px 180px 56px`;

    const emptyRow = {
        year: null,
        ...Object.fromEntries(metricFields.map((metric) => [metric, null])),
        first_sprout: "",
        first_cutting: "",
        first_flower: "",
        harvest_date: "",
    };

    const [rows, setRows] = useState([{ ...emptyRow }]);

    const numericFields = ["year", ...metricFields];

    const getYearBounds = (year) => ({
        min: `${year}-01-01`,
        max: `${year}-12-31`,
    });

    const getCalendarStartDate = (row, field, yearBounds) => {
        if (field === "first_sprout") return row.first_sprout || yearBounds?.min;
        if (field === "first_cutting") return row.first_sprout || yearBounds?.min;
        if (field === "first_flower") return row.first_cutting || row.first_sprout || yearBounds?.min;

        return row.first_flower || row.first_cutting || row.first_sprout || yearBounds?.min;
    };

    const isRowEmpty = (row) =>
        Object.values(row).every((value) => value === null || value === "");

    const isRowValid = (row) =>
        row.year !== null &&
        metricFields.every((metric) => row[metric] !== null) &&
        row.first_sprout &&
        row.first_cutting &&
        row.first_flower &&
        row.harvest_date;

  const handleChange = (index, field) => (e) => {
    const updated = [...rows];
    let value = e.target.value;

    if (numericFields.includes(field)) {
        value =
            value === ""
                ? null
                : field === "year"
                ? parseInt(value,10)
                : parseFloat(value);

        if (field === "year" && value !== null) {
            value = Math.min(value,new Date().getFullYear());
        }
    }

    updated[index][field] = value;

    if (field === "year") {
        DATE_FIELDS.forEach(dateField => {
            const currentDate = updated[index][dateField];
            if (!currentDate || !value) return;

            const [,month,day] = currentDate.split("-");
            updated[index][dateField] = `${value}-${month}-${day}`;
        });
    }

    if (field !== "year" && DATE_FIELDS.includes(field) && updated[index].year && value) {
        const [,month,day] = value.split("-");
        value = `${updated[index].year}-${month}-${day}`;
        updated[index][field] = value;
    }

    if (field === "first_sprout") {
        if (updated[index].first_cutting < value) updated[index].first_cutting = "";
        if (updated[index].first_flower < value) updated[index].first_flower = "";
        if (updated[index].harvest_date < value) updated[index].harvest_date = "";
    }

    if (field === "first_cutting") {
        if (updated[index].first_flower < value) updated[index].first_flower = "";
        if (updated[index].harvest_date < value) updated[index].harvest_date = "";
    }

    if (field === "first_flower") {
        if (updated[index].harvest_date < value) updated[index].harvest_date = "";
    }

    setRows(updated);
};

    const addRow = () => {
        const lastRow = rows[rows.length - 1];
        if (isRowEmpty(lastRow)) return;
        setRows([...rows, { ...emptyRow }]);
    };

    const removeRow = (index) => {
        const updated = rows.filter((_, i) => i !== index);
        setRows(updated.length ? updated : [{ ...emptyRow }]);
    };

    const validRows = rows.filter(isRowValid);

    const hasEnoughMetricCoverage = metricFields.every((metric) => {
        const populated = rows.filter(
            (row) => row[metric] !== null && row[metric] !== ""
        ).length;

        return populated >= 2;
    });

  const handleContinue = () => {
    const yearsMatch = validRows.every(row =>
        DATE_FIELDS.every(field =>
            row[field] && Number(row[field].split("-")[0]) === row.year
        )
    );

    if (validRows.length < 2 || !hasEnoughMetricCoverage || !yearsMatch) return;

    onNext(validRows);
};

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight={700}>
                    {translate("harvest")}
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    disabled={isRowEmpty(rows[rows.length - 1])}
                >
                    {translate("add")}
                </Button>
            </Box>

            <Box
                sx={{
                    border: "1px solid #D4E9CF",
                    borderRadius: "16px",
                    overflowX: "auto",
                    background: "#FFFFFF",
                }}
            >
                <Box sx={{ width: "fit-content", minWidth: "100%" }}>
                    <Box sx={{ px: 2, py: 1.5, background: "#24582A" }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, gap: 2 }}>
                            {["year", ...metricFields, "first_sprout", "first_cutting", "first_flower", "harvest"].map((key) => (
                                <Typography
                                    key={key}
                                    fontWeight={700}
                                    fontSize={13}
                                    color="var(--color-text-inverse)"
                                >
                                    {translate(key)}
                                </Typography>
                            ))}
                            <Box />
                        </Box>
                    </Box>

                    {rows.map((row, index) => {
                        const year = row.year;
                        const yearBounds = year ? getYearBounds(year) : null;

                        return (
                            <Box key={index} sx={{ px: 2, py: 1.5 }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, gap: 2 }}>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={row.year ?? ""}
                                        onChange={handleChange(index, "year")}
                                        inputProps={{
                                            min: 1900,
                                            max: new Date().getFullYear(),
                                        }}
                                    />

                                    {metricFields.map((field) => (
                                        <TextField
                                            key={field}
                                            size="small"
                                            type="number"
                                            value={row[field] ?? ""}
                                            onChange={handleChange(index, field)}
                                        />
                                    ))}

                                    {DATE_FIELDS.map((field) => (
                                        <TextField
                                            key={field}
                                            size="small"
                                            type="date"
                                            disabled={!year}
                                            value={row[field]}
                                            onChange={handleChange(index, field)}
                                            inputProps={{
                                                min: yearBounds?.min,
                                                max: yearBounds?.max,
                                            }}
                                        />
                                    ))}

                                    <IconButton color="error" onClick={() => removeRow(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box mt={4} display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={validRows.length < 2 || !hasEnoughMetricCoverage}
                >
                    {translate("continue")}
                </Button>
            </Box>
        </Box>
    );
}